import React, { useState } from 'react';

const SMSDemo = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [appointmentData, setAppointmentData] = useState({
    date: '2024-01-15',
    time: '10:00 AM',
    doctorName: 'Dr. Sarah Johnson',
    clinicName: 'Heart Care Center',
    address: '123 Medical Plaza, Downtown',
    mapsUrl: 'https://maps.google.com/example'
  });

  const generateSMSPreview = (type) => {
    const { date, time, doctorName, clinicName, address, mapsUrl } = appointmentData;
    
    if (type === 'confirmation') {
      return `🏥 APPOINTMENT CONFIRMED

Dear User,

Your appointment has been confirmed:

📅 Date: ${date}
🕐 Time: ${time}
👨‍⚕️ Doctor: ${doctorName}
🏥 Clinic: ${clinicName}

📍 Location: ${address}

🗺️ Directions: ${mapsUrl}

Please arrive 15 minutes early. 

For any changes, contact us.

Thank you!`;
    }
    
    if (type === 'reschedule') {
      return `📅 APPOINTMENT RESCHEDULED

Dear User,

Your appointment has been rescheduled:

NEW DETAILS:
📅 Date: ${date}
🕐 Time: ${time}
👨‍⚕️ Doctor: ${doctorName}
🏥 Clinic: ${clinicName}

📍 Location: ${address}

🗺️ Directions: ${mapsUrl}

We apologize for any inconvenience.

Thank you!`;
    }
    
    return `⏰ APPOINTMENT REMINDER

Dear User,

Reminder: You have an appointment tomorrow:

📅 Date: ${date}
🕐 Time: ${time}
👨‍⚕️ Doctor: ${doctorName}
🏥 Clinic: ${clinicName}

📍 Location: ${address}

🗺️ Directions: ${mapsUrl}

Please arrive 15 minutes early.

See you soon!`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">SMS Notification Demo</h1>
          <p className="text-gray-600">Preview how appointment confirmations, reschedules, and reminders look</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* SMS Types */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">📱 Confirmation SMS</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <pre className="text-sm text-green-800 whitespace-pre-wrap font-mono">
                  {generateSMSPreview('confirmation')}
                </pre>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">📅 Reschedule SMS</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <pre className="text-sm text-blue-800 whitespace-pre-wrap font-mono">
                  {generateSMSPreview('reschedule')}
                </pre>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">⏰ Reminder SMS</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <pre className="text-sm text-yellow-800 whitespace-pre-wrap font-mono">
                  {generateSMSPreview('reminder')}
                </pre>
              </div>
            </div>
          </div>

          {/* Customization Panel */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Customize Appointment Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={appointmentData.date}
                  onChange={(e) => setAppointmentData({...appointmentData, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                <input
                  type="text"
                  value={appointmentData.time}
                  onChange={(e) => setAppointmentData({...appointmentData, time: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 10:00 AM"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Doctor Name</label>
                <input
                  type="text"
                  value={appointmentData.doctorName}
                  onChange={(e) => setAppointmentData({...appointmentData, doctorName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Clinic Name</label>
                <input
                  type="text"
                  value={appointmentData.clinicName}
                  onChange={(e) => setAppointmentData({...appointmentData, clinicName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={appointmentData.address}
                  onChange={(e) => setAppointmentData({...appointmentData, address: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps URL</label>
                <input
                  type="url"
                  value={appointmentData.mapsUrl}
                  onChange={(e) => setAppointmentData({...appointmentData, mapsUrl: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">📲 Production Integration</h3>
              <p className="text-sm text-blue-800">
                In production, these SMS messages will be sent via Twilio, AWS SNS, or similar SMS service. 
                The system automatically includes location details and Google Maps links for easy navigation.
              </p>
            </div>
          </div>
        </div>

        {/* Features Overview */}
        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">🚀 SMS Notification Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✅</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Auto-Confirmation</h3>
              <p className="text-gray-600 text-sm">
                Automatic SMS sent when clinic staff confirms appointments with date, time, and location
              </p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📅</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Reschedule Alerts</h3>
              <p className="text-gray-600 text-sm">
                Instant notifications when appointments are rescheduled with new details and location
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🗺️</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Location Integration</h3>
              <p className="text-gray-600 text-sm">
                Includes clinic address and direct Google Maps links for easy navigation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSDemo;